import {account} from "../i18n";

// Gets a chosen picture ready to be an avatar. The API's rules (tristar-corp-api UserProfileAvatarRouteHandler):
// PNG, JPEG or GIF, at most 512×512 and 10 MB. It checks them again, and stores a still picture without metadata.
export const AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
export const AVATAR_MAX_BYTES = 10 * 1024 * 1024;
export const AVATAR_MAX_DIMENSION = 512;

export type PreparedAvatar = { image: Blob } | { problem: string };

// Larger pictures are scaled down to fit (PNG, or JPEG for a JPEG; a scaled GIF becomes a still PNG). JPEGs are
// always redrawn: that applies their EXIF orientation to the pixels, which would otherwise be lost when the API
// strips EXIF, turning phone photos on their side. Other pictures that already fit are sent as they are.
export async function prepareAvatar(file: File): Promise<PreparedAvatar> {
    if (!AVATAR_TYPES.includes(file.type)) {
        return { problem: account.profile.avatar.wrongType };
    }
    if (file.size > AVATAR_MAX_BYTES) {
        return { problem: account.profile.avatar.tooLarge };
    }
    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        return { problem: account.profile.avatar.unreadable };
    }
    try {
        const scale = Math.min(1, AVATAR_MAX_DIMENSION / bitmap.width, AVATAR_MAX_DIMENSION / bitmap.height);
        const isJpeg = file.type === 'image/jpeg';
        if (scale === 1 && !isJpeg) {
            return { image: file };
        }
        const width = Math.max(1, Math.floor(bitmap.width * scale));
        const height = Math.max(1, Math.floor(bitmap.height * scale));
        const canvas = downscale(bitmap, width, height);
        const image = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, isJpeg ? 'image/jpeg' : 'image/png', 0.9));
        return image ? { image } : { problem: account.profile.avatar.notResized };
    } finally {
        bitmap.close();
    }
}

// Halves until the last step is at most 2×, which keeps a big photo sharp where one smoothed step would alias.
function downscale(source: ImageBitmap, width: number, height: number): HTMLCanvasElement {
    let current: CanvasImageSource = source;
    let currentWidth = source.width;
    let currentHeight = source.height;
    while (currentWidth / 2 >= width && currentHeight / 2 >= height) {
        currentWidth = Math.floor(currentWidth / 2);
        currentHeight = Math.floor(currentHeight / 2);
        current = draw(current, currentWidth, currentHeight);
    }
    return draw(current, width, height);
}

function draw(source: CanvasImageSource, width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d')!;
    context.imageSmoothingQuality = 'high';
    context.drawImage(source, 0, 0, width, height);
    return canvas;
}
